import React, { useMemo } from 'react';
import { Select, Spin, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useDebounce } from '@react-hook/debounce';
import { useCountryDestinationsSearch } from 'api/destinations.api';
import type { DestinationSchema } from 'typings/destinations/DestinationSchema';
import styles from './DestinationCountrySelector.module.css';

type DestinationCountrySelectorValue = Pick<DestinationSchema, 'country' | 'country_code'>;

type DestinationCountrySelectorProps = Readonly<{
    value: DestinationCountrySelectorValue | null;
    onChange: (countryDestination: DestinationSchema | null) => void;
    hasError?: boolean;
}>;

export function DestinationCountrySelector({ value, onChange, hasError = false }: DestinationCountrySelectorProps) {
    const [searchQueryDebounced, setSearchQueryDebounced] = useDebounce(value?.country ?? '', 300);
    const { countryDestinations, isLoading, isValidating } = useCountryDestinationsSearch(searchQueryDebounced);
    const hasSearchQuery = searchQueryDebounced.trim().length >= 3;

    const selectedValue = useMemo(() => {
        if (!value?.country) {
            return null;
        }

        const matchingCountry = countryDestinations.find(
            (countryDestination) =>
                countryDestination.country_code === value.country_code ||
                countryDestination.country === value.country ||
                countryDestination.name === value.country
        );

        return matchingCountry?.id ?? value.country;
    }, [countryDestinations, value]);
    const notFoundContent = getNotFoundContent(isLoading, hasSearchQuery);

    return (
        <Select
            showSearch
            value={selectedValue}
            placeholder={isLoading ? 'Loading...' : 'Search for a country'}
            notFoundContent={<div className={styles.notFoundContent}>{notFoundContent}</div>}
            filterOption={false}
            onSearch={(text) => setSearchQueryDebounced(text)}
            onChange={(selectedCountryId) => {
                if (!selectedCountryId) {
                    onChange(null);
                    return;
                }

                const countryDestination = countryDestinations.find((item) => item.id === selectedCountryId);
                onChange(countryDestination ?? null);
            }}
            className={styles.root}
            size='large'
            loading={isValidating}
            status={hasError ? 'error' : undefined}
            allowClear={!!value?.country}
            optionLabelProp='label'
            suffixIcon={<SearchOutlined />}>
            {countryDestinations.map((countryDestination) => {
                const label = countryDestination.country ?? countryDestination.name ?? 'Unknown country';

                return (
                    <Select.Option key={countryDestination.id} value={countryDestination.id} label={label}>
                        {label}
                    </Select.Option>
                );
            })}
        </Select>
    );
}

function getNotFoundContent(isLoading: boolean, hasSearchQuery: boolean) {
    if (isLoading) {
        return <Spin size='small' />;
    }

    return (
        <Typography.Text type='secondary'>
            {hasSearchQuery ? 'No country destinations found' : 'Type at least 3 characters'}
        </Typography.Text>
    );
}
